/**
 * El **único** sitio de la aplicación que habla HTTP.
 *
 * Ningún componente hace `fetch`. Concentrarlo aquí es lo que permite tener un solo
 * lugar donde vive la autenticación, un solo formato de error y un solo punto donde
 * cambiar de esquema de credenciales — y es lo que evita que dentro de tres meses
 * haya cuatro maneras distintas de tratar un 401.
 */

import { clearSesion, getToken } from "@/api/auth"
import { gatewayUrl } from "@/api/config"

export class ApiError extends Error {
  // Campos declarados y asignados a mano: `erasableSyntaxOnly` prohíbe las
  // propiedades de constructor porque el bundler solo BORRA tipos, no transforma
  // código. Misma razón por la que tampoco se pueden usar `enum` ni `namespace`.
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = "ApiError"
    this.status = status
    this.detail = detail
  }

  /** Un 401 significa "vuelve a entrar", y llega por más motivos que caducar.
   *
   * También cuando a alguien lo expulsan o le cambian el rol: el gateway invalida sus
   * sesiones al momento, por diseño. Por eso se trata igual que un token vencido, y en
   * un solo sitio.
   */
  get esNoAutorizado(): boolean {
    return this.status === 401
  }

  /** 429: has ido demasiado rápido o tienes demasiadas peticiones en vuelo. */
  get esDemasiadasPeticiones(): boolean {
    return this.status === 429
  }
}

type Opciones = {
  method?: "GET" | "POST" | "PUT" | "DELETE"
  body?: unknown
  signal?: AbortSignal
}

export async function request<T>(path: string, opts: Opciones = {}): Promise<T> {
  const token = getToken()

  const respuesta = await fetch(`${gatewayUrl()}${path}`, {
    method: opts.method ?? "GET",
    headers: {
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      ...(opts.body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  })

  if (respuesta.status === 401) {
    // Se limpia aquí y no en cada pantalla: si el token dejó de valer, seguir
    // mandándolo solo genera más 401. `clearSesion` avisa además a la aplicación, que
    // es lo que devuelve al operador a la pantalla de entrada sin que cada página
    // tenga que acordarse de comprobarlo.
    clearSesion()
    throw new ApiError(401, "sesión no válida")
  }

  if (!respuesta.ok) {
    throw new ApiError(respuesta.status, await detalleDe(respuesta))
  }

  if (respuesta.status === 204) return undefined as T
  return (await respuesta.json()) as T
}

async function detalleDe(respuesta: Response): Promise<string> {
  try {
    const cuerpo: unknown = await respuesta.json()
    if (typeof cuerpo === "object" && cuerpo !== null && "detail" in cuerpo) {
      const d = (cuerpo as { detail: unknown }).detail
      if (typeof d === "string") return d
    }
  } catch {
    // Cuerpo no-JSON (un error del proxy, por ejemplo). Se cae al genérico.
  }
  return `error ${respuesta.status}`
}
